import {
  buildOutboundMediaAcceptAttribute,
  findOutboundMediaCapability,
  isNormalizableRecorderMime,
  isOutboundMediaMimeAllowed,
  type OutboundMediaAttachmentType,
  type PlatformTypeName,
} from "@multi-chat/shared";

export { buildOutboundMediaAcceptAttribute };

type OutboundMediaCapabilitiesInput = {
  platform: PlatformTypeName;
  entries: readonly {
    attachmentType: string;
    maxBytes: number;
    allowedMimeTypes: readonly string[];
  }[];
};

export type OutboundMediaValidationErrorCode =
  | "OUTBOUND_MEDIA_EMPTY"
  | "OUTBOUND_MEDIA_UNSUPPORTED_TYPE"
  | "OUTBOUND_MEDIA_TOO_LARGE"
  | "OUTBOUND_MEDIA_INVALID_FILENAME";

export type OutboundMediaValidationResult =
  | {
      ok: true;
      attachmentType: OutboundMediaAttachmentType;
      mimeType: string;
    }
  | {
      ok: false;
      code: OutboundMediaValidationErrorCode;
    };

const CONTROL_OR_DEL_CHARS = /[\x00-\x1f\x7f]/;

/** Mirrors backend `inferOutboundAttachmentType` for client-side guards. */
export function inferOutboundAttachmentType(
  mimeType: string,
  platform: PlatformTypeName,
): OutboundMediaAttachmentType | null {
  const mime = mimeType.toLowerCase().split(";")[0]?.trim() ?? "";
  if (!mime) {
    return null;
  }

  if (mime.startsWith("image/")) {
    if (mime === "image/webp" && platform === "WHATSAPP") {
      return "STICKER";
    }
    return "IMAGE";
  }
  if (mime.startsWith("video/")) {
    return "VIDEO";
  }
  if (mime.startsWith("audio/")) {
    return "AUDIO";
  }
  if (
    mime === "application/pdf" ||
    mime === "text/plain" ||
    mime.startsWith("application/vnd.") ||
    mime === "application/msword"
  ) {
    return "DOCUMENT";
  }
  return null;
}

export function validateOutboundMediaFile(input: {
  file: File;
  capabilities: OutboundMediaCapabilitiesInput;
  recordedVoice?: boolean;
}): OutboundMediaValidationResult {
  const { file, capabilities } = input;
  const platform = capabilities.platform;

  if (file.size <= 0) {
    return { ok: false, code: "OUTBOUND_MEDIA_EMPTY" };
  }

  const mimeType = file.type.toLowerCase().split(";")[0]?.trim() ?? "";
  if (!mimeType) {
    return { ok: false, code: "OUTBOUND_MEDIA_UNSUPPORTED_TYPE" };
  }

  let attachmentType = inferOutboundAttachmentType(mimeType, platform);
  if (input.recordedVoice && mimeType.startsWith("audio/")) {
    attachmentType = "AUDIO";
  }
  if (!attachmentType) {
    return { ok: false, code: "OUTBOUND_MEDIA_UNSUPPORTED_TYPE" };
  }

  const capability = findOutboundMediaCapability(platform, attachmentType);
  if (!capability) {
    return { ok: false, code: "OUTBOUND_MEDIA_UNSUPPORTED_TYPE" };
  }

  if (file.size > capability.maxBytes) {
    return { ok: false, code: "OUTBOUND_MEDIA_TOO_LARGE" };
  }

  if (input.recordedVoice) {
    if (!isNormalizableRecorderMime(mimeType)) {
      return { ok: false, code: "OUTBOUND_MEDIA_UNSUPPORTED_TYPE" };
    }
  } else if (!isOutboundMediaMimeAllowed(platform, attachmentType, mimeType)) {
    return { ok: false, code: "OUTBOUND_MEDIA_UNSUPPORTED_TYPE" };
  }

  const fileName = file.name.trim();
  if (fileName.length > 0 && CONTROL_OR_DEL_CHARS.test(fileName)) {
    return { ok: false, code: "OUTBOUND_MEDIA_INVALID_FILENAME" };
  }

  return { ok: true, attachmentType, mimeType };
}

export function outboundMediaValidationErrorKey(
  code: OutboundMediaValidationErrorCode,
): string {
  switch (code) {
    case "OUTBOUND_MEDIA_EMPTY":
      return "inbox.composer.attachmentErrors.empty";
    case "OUTBOUND_MEDIA_TOO_LARGE":
      return "inbox.composer.attachmentErrors.tooLarge";
    case "OUTBOUND_MEDIA_INVALID_FILENAME":
      return "inbox.composer.attachmentErrors.invalidFilename";
    case "OUTBOUND_MEDIA_UNSUPPORTED_TYPE":
      return "inbox.composer.attachmentErrors.unsupportedType";
    default: {
      const _exhaustive: never = code;
      return _exhaustive;
    }
  }
}
